import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';
import { Swords, TrendingUp, Trophy, BookOpen, Flame, ArrowRight } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HeadToHead = ({ friends, isDark }) => {
  const { token } = useAuth();
  const [selectedFriend, setSelectedFriend] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const compare = async () => {
    if (!selectedFriend) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/leaderboard/head-to-head?friend_id=${selectedFriend}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (e) {
      toast({ title: 'Failed to compare', variant: 'destructive' });
    } finally { setLoading(false); }
  };

  const StatRow = ({ label, icon: Icon, myVal, friendVal }) => {
    const myWin = Number(myVal) > Number(friendVal);
    const friendWin = Number(friendVal) > Number(myVal);
    return (
      <div className="grid grid-cols-3 items-center py-2 border-b border-gray-100 last:border-0">
        <div className={`text-right text-sm font-bold ${myWin ? 'text-green-600' : 'text-gray-700'}`}>{myVal}</div>
        <div className="text-center text-xs text-gray-500 flex items-center justify-center gap-1">
          <Icon className="h-3.5 w-3.5" /> {label}
        </div>
        <div className={`text-left text-sm font-bold ${friendWin ? 'text-green-600' : 'text-gray-700'}`}>{friendVal}</div>
      </div>
    );
  };

  return (
    <div className="max-w-2xl mx-auto space-y-4" data-testid="head-to-head">
      <h2 className={`text-xl font-bold flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
        <Swords className="h-5 w-5 text-indigo-600" /> Head-to-Head
      </h2>

      <Card>
        <CardContent className="p-4">
          <div className="flex gap-2">
            <Select value={selectedFriend} onValueChange={setSelectedFriend}>
              <SelectTrigger className="flex-1" data-testid="h2h-select-friend">
                <SelectValue placeholder="Select a friend to compare" />
              </SelectTrigger>
              <SelectContent>
                {(friends || []).map(f => (
                  <SelectItem key={f.id} value={f.id}>{f.username}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={compare} disabled={!selectedFriend || loading} data-testid="h2h-compare-btn">
              {loading ? 'Loading...' : 'Compare'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {data && (
        <Card>
          <CardContent className="p-4">
            {/* Player headers */}
            <div className="grid grid-cols-3 items-center mb-4">
              <div className="flex flex-col items-center">
                <Avatar className="h-14 w-14 mb-1">
                  <AvatarImage src={data.me.profile_image} />
                  <AvatarFallback style={{ backgroundColor: data.me.banner_color }} className="text-white font-bold">{data.me.username[0].toUpperCase()}</AvatarFallback>
                </Avatar>
                <span className="text-sm font-bold">{data.me.username}</span>
                <span className="text-xs text-gray-500">You</span>
              </div>
              <div className="flex items-center justify-center">
                <Badge className="bg-indigo-600 text-white px-3 py-1 text-sm">VS</Badge>
              </div>
              <div className="flex flex-col items-center">
                <Avatar className="h-14 w-14 mb-1">
                  <AvatarImage src={data.friend.profile_image} />
                  <AvatarFallback style={{ backgroundColor: data.friend.banner_color }} className="text-white font-bold">{data.friend.username[0].toUpperCase()}</AvatarFallback>
                </Avatar>
                <span className="text-sm font-bold">{data.friend.username}</span>
              </div>
            </div>

            {/* Stats comparison */}
            <StatRow label="Matches" icon={Swords} myVal={data.me.total_matches} friendVal={data.friend.total_matches} />
            <StatRow label="Wins" icon={Trophy} myVal={data.me.wins} friendVal={data.friend.wins} />
            <StatRow label="Win Rate" icon={TrendingUp} myVal={`${data.me.win_rate}%`} friendVal={`${data.friend.win_rate}%`} />
            <StatRow label="Best Streak" icon={Flame} myVal={data.me.best_streak} friendVal={data.friend.best_streak} />
            <StatRow label="Diary" icon={BookOpen} myVal={data.me.diary_entries} friendVal={data.friend.diary_entries} />
          </CardContent>
        </Card>
      )}

      {!data && !loading && friends?.length === 0 && (
        <Card><CardContent className="py-10 text-center text-gray-500 text-sm">Add friends first to compare stats</CardContent></Card>
      )}
    </div>
  );
};

export default HeadToHead;
